/**
 * `GET /public/tenant-context` — the pre-login tenant lookup.
 *
 * Unauthenticated, so it takes no client. A 404 means "this URL addresses no
 * tenant", which is a normal answer rather than an error, and comes back as
 * `null`.
 */

import { API_BASE } from '../api';

export interface TenantContext {
    tenantId: string;
    /** The tenant's **current** slug. Differs from the one in the URL when a
     *  retired slug was used — see `stale`. */
    slug: string;
    name: string;
    type: string;
    status: 'active' | 'suspended' | 'archived';
    persona: 'student' | 'teacher' | 'admin';
    branding?: { logoUrl?: string };
    /** Where this tenant should be addressed, in the form the request arrived
     *  in. The redirect target when `stale`. */
    canonicalUrl: string;
    /** Whether a user may self-register into this tenant. False for every real
     *  tenant — membership comes from an invite. */
    allowsSelfRegistration: boolean;
    /** Where self-signup *is* open (the shared host for this persona), so a
     *  refusal on a tenant URL can offer a way forward. Absent if the server
     *  could not resolve it. */
    sharedAppUrl?: string;
    /** The URL used a retired slug; redirect to `canonicalUrl`. */
    stale: boolean;
}

/**
 * Resolve the tenant for this URL. Pass `slug` for the path form; omit it for
 * the host form, where the server reads the request `Origin`.
 *
 * Returns `null` for 404 (no tenant addresses this URL) and also for a network
 * or server failure — branding is not worth blocking a login screen over, so a
 * failed lookup degrades to the default brand rather than an error page.
 */
export async function fetchTenantContext(slug?: string): Promise<TenantContext | null> {
    const query = slug ? `?t=${encodeURIComponent(slug)}` : '';
    try {
        const response = await fetch(`${API_BASE}/public/tenant-context${query}`);
        if (!response.ok) return null;
        const json = await response.json();
        return (json?.data ?? null) as TenantContext | null;
    } catch {
        return null;
    }
}
